import type { PillStatus } from '../primitives/Pill';
import {
  COMPLETED_STATUSES,
  GALLERY_WORK_ITEM_TYPES,
  type DodCriterion,
  type ProjectDocument,
  type WorkItemRow
} from './types';

/** Parse the `definition_of_done` jsonb into a sorted, well-typed array. */
export function parseDod(raw: unknown): DodCriterion[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((c, i) => {
      const o = (c ?? {}) as Record<string, unknown>;
      return {
        id: typeof o.id === 'string' ? o.id : `dod-${i}`,
        text: typeof o.text === 'string' ? o.text : '',
        met: o.met === true,
        position: typeof o.position === 'number' ? o.position : i
      };
    })
    .sort((a, b) => a.position - b.position);
}

/**
 * Canonical normalisation for free-text work-item / project statuses and stage
 * names: `trim → lowercase → underscores-to-spaces`. The single source of the
 * transform that `statusToPill` and `rollUpWorkItems` both match on (so neither
 * re-lists it).
 */
export function normaliseStatus(status: string): string {
  return status.trim().toLowerCase().replace(/_/g, ' ');
}

/** Map a free-text work-item / project status to a Pill variant. */
export function statusToPill(status: string): PillStatus {
  const s = normaliseStatus(status);
  if (COMPLETED_STATUSES.has(s) || s === 'archived') return 'done';
  if (s.includes('progress') || s === 'active' || s === 'now') return 'progress';
  if (s.includes('review')) return 'review';
  if (s.includes('block')) return 'blocked';
  if (s.includes('cancel')) return 'cancel';
  return 'todo';
}

const PILL_TONE: Record<PillStatus, string> = {
  todo: 'var(--st-todo)',
  progress: 'var(--st-progress)',
  review: 'var(--st-review)',
  blocked: 'var(--st-blocked)',
  done: 'var(--st-done)',
  cancel: 'var(--st-cancel)',
  draft: 'var(--st-todo)',
  published: 'var(--st-done)',
  active: 'var(--st-done)',
  pending: 'var(--st-progress)',
  expired: 'var(--st-cancel)',
  suspended: 'var(--st-warn)'
};

/** The `--st-*` token (as a `var(...)` string) for a status — drives the
 *  segmented-status `--tone`. */
export function statusToTone(status: string): string {
  return PILL_TONE[statusToPill(status)];
}

/** True when a work item's status is terminal-completed. */
export function isCompleted(item: WorkItemRow): boolean {
  return COMPLETED_STATUSES.has(item.status.trim().toLowerCase());
}

/** done/total over the gallery work-item types (story/bug/task). */
export function computeProgress(items: ReadonlyArray<{ type: string; status: string }>): {
  done: number;
  total: number;
} {
  const counted = items.filter((i) => GALLERY_WORK_ITEM_TYPES.has(i.type));
  return {
    done: counted.filter((i) => COMPLETED_STATUSES.has(i.status.trim().toLowerCase())).length,
    total: counted.length
  };
}

/** Split joined docs into Mockups (canvas) vs Documents (everything else). */
export function splitProjectDocs(docs: ReadonlyArray<ProjectDocument>): {
  regular: ProjectDocument[];
  canvas: ProjectDocument[];
} {
  const regular: ProjectDocument[] = [];
  const canvas: ProjectDocument[] = [];
  for (const d of docs) (d.typeKey === 'canvas' ? canvas : regular).push(d);
  return { regular, canvas };
}

/**
 * Stable short reference code for display (e.g. "PRJ-3F9A", "WI-1B7C").
 * There is no human-friendly ref-id column, so we derive a deterministic code
 * from the row UUID for visual parity with the design mocks.
 */
export function refCode(prefix: string, id: string): string {
  const hex = id.replace(/[^0-9a-f]/gi, '').slice(0, 4).toUpperCase();
  return `${prefix}-${hex || '0000'}`;
}

/** Direct children of a work item (one level). */
export function directChildren(
  items: ReadonlyArray<WorkItemRow>,
  parentId: string
): WorkItemRow[] {
  return items.filter((i) => i.parent_work_item_id === parentId);
}
